import { FeedbackCategory, FeedbackStatus } from '@repo/shared'
import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
  Input,
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
  Skeleton,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@repo/ui'
import { useDebouncedValue } from '@tanstack/react-pacer'
import { createFileRoute } from '@tanstack/react-router'
import { format } from 'date-fns'
import { Search } from 'lucide-react'
import { useState } from 'react'
import { useTranslation } from 'react-i18next'

import type { FeedbackListItem } from '@repo/api'

import { ListPagination } from '@/components/shared/list-pagination'
import { FeedbackCategoryBadge } from '@/features/feedback/components/feedback-category-badge'
import FeedbackDetailModal from '@/features/feedback/components/feedback-detail-modal'
import { FeedbackStatusBadge } from '@/features/feedback/components/feedback-status-badge'
import { SentimentIcon } from '@/features/feedback/components/sentiment-icon'
import { useGetFeedbacksPaginated } from '@/features/feedback/hooks'

export const Route = createFileRoute('/admin/feedback/')({
  component: AdminFeedbackListPage,
})

const FILTER_ALL = 'all'

const STATUS_FILTER_OPTIONS: FeedbackStatus[] = [
  FeedbackStatus.New,
  FeedbackStatus.Triaged,
  FeedbackStatus.Resolved,
]

function FeedbackRow({
  item,
  onOpen,
}: {
  item: FeedbackListItem
  onOpen: (item: FeedbackListItem) => void
}) {
  const userName = item.user
    ? `${item.user.firstName} ${item.user.lastName}`
    : '—'
  return (
    <TableRow
      className="hover:bg-muted/40 cursor-pointer"
      onClick={() => onOpen(item)}
    >
      <TableCell className="w-12">
        <SentimentIcon sentiment={item.sentiment} />
      </TableCell>
      <TableCell className="w-28">
        <FeedbackStatusBadge status={item.status} />
      </TableCell>
      <TableCell className="w-28">
        <FeedbackCategoryBadge category={item.category} />
      </TableCell>
      <TableCell className="max-w-md align-top">
        <p className="line-clamp-3 break-words whitespace-pre-wrap">
          {item.message}
        </p>
      </TableCell>
      <TableCell>
        <div className="flex flex-col">
          <span className="text-sm font-medium">{userName}</span>
          {item.user?.email && (
            <span className="text-muted-foreground text-xs">
              {item.user.email}
            </span>
          )}
        </div>
      </TableCell>
      <TableCell>
        <span className="text-sm">{item.organization?.name ?? '—'}</span>
      </TableCell>
      <TableCell className="text-muted-foreground max-w-[200px] truncate text-xs">
        {item.pageUrl ?? '—'}
      </TableCell>
      <TableCell className="text-muted-foreground text-xs whitespace-nowrap">
        {format(new Date(item.createdAt), 'PP p')}
      </TableCell>
    </TableRow>
  )
}

function FeedbackCard({
  item,
  onOpen,
}: {
  item: FeedbackListItem
  onOpen: (item: FeedbackListItem) => void
}) {
  const userName = item.user
    ? `${item.user.firstName} ${item.user.lastName}`
    : '—'
  return (
    <Card
      className="bg-background hover:bg-muted/40 cursor-pointer"
      onClick={() => onOpen(item)}
    >
      <CardContent className="space-y-3 p-4">
        <div className="flex items-start justify-between gap-2">
          <div className="flex flex-wrap items-center gap-2">
            <SentimentIcon sentiment={item.sentiment} />
            <FeedbackStatusBadge status={item.status} />
            <FeedbackCategoryBadge category={item.category} />
          </div>
          <span className="text-muted-foreground text-xs whitespace-nowrap">
            {format(new Date(item.createdAt), 'PP p')}
          </span>
        </div>
        <p className="text-sm break-words whitespace-pre-wrap">
          {item.message}
        </p>
        <div className="border-border space-y-1 border-t pt-2">
          <p className="text-sm font-medium">{userName}</p>
          {item.user?.email && (
            <p className="text-muted-foreground text-xs">{item.user.email}</p>
          )}
          {item.organization?.name && (
            <p className="text-muted-foreground text-xs">
              {item.organization.name}
            </p>
          )}
          {item.pageUrl && (
            <p className="text-muted-foreground truncate text-xs">
              {item.pageUrl}
            </p>
          )}
        </div>
      </CardContent>
    </Card>
  )
}

function AdminFeedbackListPage() {
  const { t } = useTranslation('feedback')

  const [searchQuery, setSearchQuery] = useState('')
  const [debouncedSearch] = useDebouncedValue(searchQuery, { wait: 300 })
  const [statusFilter, setStatusFilter] = useState<string>(FILTER_ALL)
  const [categoryFilter, setCategoryFilter] = useState<string>(FILTER_ALL)
  const [page, setPage] = useState(1)
  const [pageSize, setPageSize] = useState(10)
  const [selected, setSelected] = useState<FeedbackListItem | null>(null)

  const { data, isLoading } = useGetFeedbacksPaginated({
    page,
    pageSize,
    search: debouncedSearch || undefined,
    status:
      statusFilter === FILTER_ALL
        ? undefined
        : (statusFilter as FeedbackStatus),
    category:
      categoryFilter === FILTER_ALL
        ? undefined
        : (categoryFilter as FeedbackCategory),
  })

  const items = data?.items ?? []

  const handlePageSizeChange = (size: number) => {
    setPageSize(size)
    setPage(1)
  }

  const handleSearchChange = (value: string) => {
    setSearchQuery(value)
    setPage(1)
  }

  const handleStatusFilterChange = (value: string) => {
    setStatusFilter(value)
    setPage(1)
  }

  const handleCategoryFilterChange = (value: string) => {
    setCategoryFilter(value)
    setPage(1)
  }

  return (
    <div className="flex-1 space-y-4">
      <Card className="bg-background max-md:rounded-none max-md:border-0 max-md:bg-transparent max-md:shadow-none">
        <CardHeader className="max-md:px-0">
          <div className="flex flex-col gap-3 md:flex-row md:items-center md:justify-between">
            <div>
              <CardTitle>{t('allFeedback')}</CardTitle>
              <CardDescription>
                {data?.totalCount ?? 0} {t('totalFeedback')}
              </CardDescription>
            </div>
            <div className="flex flex-col gap-2 md:flex-row md:items-center">
              <Select
                value={statusFilter}
                onValueChange={handleStatusFilterChange}
              >
                <SelectTrigger className="h-11 w-full md:w-[180px]">
                  <SelectValue placeholder={t('status.filterPlaceholder')} />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value={FILTER_ALL}>
                    {t('status.allStatuses')}
                  </SelectItem>
                  {STATUS_FILTER_OPTIONS.map((s) => (
                    <SelectItem key={s} value={s}>
                      {t(`status.${s}`)}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
              <Select
                value={categoryFilter}
                onValueChange={handleCategoryFilterChange}
              >
                <SelectTrigger className="h-11 w-full md:w-[180px]">
                  <SelectValue placeholder={t('category.filterPlaceholder')} />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value={FILTER_ALL}>
                    {t('category.allCategories')}
                  </SelectItem>
                  {Object.values(FeedbackCategory).map((c) => (
                    <SelectItem key={c} value={c}>
                      {t(`category.${c}`)}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
              <div className="relative w-full md:w-64">
                <Search className="text-muted-foreground absolute top-1/2 left-3 h-4 w-4 -translate-y-1/2" />
                <Input
                  placeholder={t('searchFeedback')}
                  value={searchQuery}
                  onChange={(e) => handleSearchChange(e.target.value)}
                  className="h-11 pl-9"
                />
              </div>
            </div>
          </div>
        </CardHeader>
        <CardContent className="px-4 max-md:px-0">
          {isLoading ? (
            <div className="space-y-3">
              {[1, 2, 3, 4].map((i) => (
                <Skeleton key={i} className="h-20 w-full rounded-md" />
              ))}
            </div>
          ) : data && items.length > 0 ? (
            <>
              <div className="hidden overflow-hidden rounded-lg border md:block">
                <Table>
                  <TableHeader>
                    <TableRow className="bg-muted/50 hover:bg-muted/50">
                      <TableHead className="w-12">{t('table.mood')}</TableHead>
                      <TableHead className="w-28">
                        {t('table.status')}
                      </TableHead>
                      <TableHead className="w-28">
                        {t('table.category')}
                      </TableHead>
                      <TableHead>{t('table.message')}</TableHead>
                      <TableHead>{t('table.submittedBy')}</TableHead>
                      <TableHead>{t('table.organization')}</TableHead>
                      <TableHead>{t('table.page')}</TableHead>
                      <TableHead>{t('table.date')}</TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {items.map((item) => (
                      <FeedbackRow
                        key={item.id}
                        item={item}
                        onOpen={setSelected}
                      />
                    ))}
                  </TableBody>
                </Table>
              </div>
              <div className="space-y-3 md:hidden">
                {items.map((item) => (
                  <FeedbackCard
                    key={item.id}
                    item={item}
                    onOpen={setSelected}
                  />
                ))}
              </div>
              <ListPagination
                page={data.page}
                totalPages={data.totalPages}
                pageSize={data.pageSize}
                totalCount={data.totalCount}
                hasPrevious={data.hasPrevious}
                hasNext={data.hasNext}
                onPageChange={setPage}
                onPageSizeChange={handlePageSizeChange}
                className="mt-4 px-4 max-md:px-0"
              />
            </>
          ) : (
            <div className="py-12 text-center">
              <p className="text-muted-foreground text-sm">
                {t('noFeedbackFound')}
              </p>
            </div>
          )}
        </CardContent>
      </Card>

      <FeedbackDetailModal
        item={selected}
        open={selected !== null}
        onOpenChange={(open) => {
          if (!open) setSelected(null)
        }}
      />
    </div>
  )
}
